import {
    ApiContentSection,
    ApiFullLandingPageLink,
    ApiSectionType,
    HIGHLIGHTED_PRODUCT_HASH,
    toApiFullLandingPageLink,
} from '@storefronts/common-types'
import { useContext, useEffect, useMemo, useState } from 'react'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import { useCombinedProductHistoryAndLandingPageLinks } from '../../../utils/useCombinedProductHistory'
import { CustomerPhotos } from '../reviews/CustomerPhotos'
import { SectionContainer } from './Shared'

type ReviewsImagesSectionProps = Extract<
    ApiContentSection,
    { type: ApiSectionType.REVIEWS_IMAGES }
>

export const ReviewsImagesSection: React.FC<{
    section: ReviewsImagesSectionProps
}> = ({ section }) => {
    const { fetchAndCacheLinksForHashes, getCachedOrderedLinksForHashes } =
        useContext(LandingPageLinksContext)

    const highlightsMainProductSlot =
        section.linkHash === HIGHLIGHTED_PRODUCT_HASH

    const cachedOrderedLinksForHash = useMemo(() => {
        if (highlightsMainProductSlot) return []
        return getCachedOrderedLinksForHashes([section.linkHash]).orderedLinks
    }, [
        getCachedOrderedLinksForHashes,
        highlightsMainProductSlot,
        section.linkHash,
    ])

    const combinedOrderedPageLinks =
        useCombinedProductHistoryAndLandingPageLinks()

    const lpLinkFromLandingData = toApiFullLandingPageLink(
        highlightsMainProductSlot
            ? (combinedOrderedPageLinks[0] ?? null)
            : (cachedOrderedLinksForHash[0] ??
                  combinedOrderedPageLinks.find(
                      (l) => l.linkHash === section.linkHash,
                  ) ??
                  null),
    )

    const [
        landingPageLinkFromFetchedBatch,
        setLandingPageLinkFromFetchedBatch,
    ] = useState<ApiFullLandingPageLink | null>(null)

    useEffect(() => {
        if (lpLinkFromLandingData || highlightsMainProductSlot) return

        let isCancelled = false
        fetchAndCacheLinksForHashes([section.linkHash])
            .then((links) => {
                if (!isCancelled)
                    setLandingPageLinkFromFetchedBatch(
                        toApiFullLandingPageLink(links[0] ?? null),
                    )
            })
            .catch(() => {
                if (!isCancelled) setLandingPageLinkFromFetchedBatch(null)
            })
        return () => {
            isCancelled = true
        }
    }, [
        fetchAndCacheLinksForHashes,
        highlightsMainProductSlot,
        lpLinkFromLandingData,
        section.linkHash,
    ])

    const lpLinkFromFetchedBatch =
        landingPageLinkFromFetchedBatch?.linkHash === section.linkHash
            ? landingPageLinkFromFetchedBatch
            : null

    const lpLink = lpLinkFromLandingData ?? lpLinkFromFetchedBatch
    if (!lpLink) return null

    return (
        <SectionContainer>
            <CustomerPhotos
                key={`${lpLink.linkHash}-${section.variantShopifyProductId ?? ''}`}
                linkHash={lpLink.linkHash}
                initialMedia={lpLink.reviewMedia}
                shopifyProductId={section.variantShopifyProductId ?? null}
            />
        </SectionContainer>
    )
}
